/**
 * Building Review API Client
 *
 * @remarks
 * Cliente HTTP para endpoints de revisión manual de edificios.
 * Usa el mismo patrón de auth que buildings-api.ts (token desde auth-storage).
 */

import axios, { type AxiosInstance } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type ManualPopulationStatus = 'auto' | 'include' | 'exclude';
export type ManualFunctionalClass  = 'residential' | 'non_residential' | 'mixed';
export type EffectivePopulationStatus =
  | 'auto_eligible'
  | 'auto_excluded'
  | 'auto_unknown'
  | 'manual_included'
  | 'manual_excluded';

export interface BuildingReviewResponse {
  hasReview:                 boolean;
  buildingCentroidId:        string;
  isUserReviewed:            boolean;
  hasManualOverride:         boolean;
  manualPopulationStatus:    ManualPopulationStatus;
  effectivePopulationStatus: EffectivePopulationStatus;
  manualFunctionalClass:     ManualFunctionalClass | null;
  effectiveFunctionalClass:  string | null;
  manualLevels:              number | null;
  effectiveLevels:           number | null;
  reviewNote:                string | null;
  reviewedAt:                string | null;
  reviewedBy:                string | null;
  createdAt:                 string | null;
  updatedAt:                 string | null;
}

export interface PatchBuildingReviewPayload {
  isUserReviewed?:         boolean;
  manualPopulationStatus?: ManualPopulationStatus;
  manualFunctionalClass?:  ManualFunctionalClass | null;
  manualLevels?:           number | null;
  reviewNote?:             string | null;
}

// ─── Cliente ──────────────────────────────────────────────────────────────────

function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(
    (config) => {
      const authStore = localStorage.getItem('auth-storage');
      if (authStore) {
        try {
          const parsed = JSON.parse(authStore) as { state?: { token?: string } };
          const token = parsed.state?.token;
          if (token) config.headers.Authorization = `Bearer ${token}`;
        } catch {
          // silenciar
        }
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  return instance;
}

const api = createApiInstance();

// ─── Métodos ──────────────────────────────────────────────────────────────────

export const buildingReviewApi = {
  /**
   * Obtiene el estado de revisión de un edificio.
   * Si no existe revisión, retorna hasReview=false con defaults automáticos.
   */
  async getReview(
    projectId: string,
    buildingId: string,
  ): Promise<BuildingReviewResponse> {
    const res = await api.get<BuildingReviewResponse>(
      `/projects/${projectId}/buildings/${buildingId}/review`,
    );
    return res.data;
  },

  /**
   * Crea o actualiza (upsert) la revisión de un edificio.
   * Solo los campos presentes en el payload se modifican.
   * Enviar null en manualFunctionalClass o manualLevels quita el override.
   */
  async patchReview(
    projectId: string,
    buildingId: string,
    payload: PatchBuildingReviewPayload,
  ): Promise<BuildingReviewResponse> {
    const res = await api.patch<BuildingReviewResponse>(
      `/projects/${projectId}/buildings/${buildingId}/review`,
      payload,
    );
    return res.data;
  },

  /**
   * Elimina la revisión manual, dejando el edificio en estado automático.
   * Idempotente: no lanza error si no existe revisión.
   */
  async deleteReview(
    projectId: string,
    buildingId: string,
  ): Promise<void> {
    await api.delete(
      `/projects/${projectId}/buildings/${buildingId}/review`,
    );
  },
};
